// Top-level layout + routing. Each destination is a URL (/home, /plan,
// /grocery, /recipes, and the menu's pages) -- shareable, bookmarkable, and
// back/forward-navigable. A page with subtabs (Plan) nests them as child
// routes under a SubtabLayout; see src/lib/navigation.ts. The tabs and menu
// themselves are in components/AppNav.tsx (redesign slice 2).
import { useState, useEffect, useLayoutEffect, useRef } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { MealPlannerProvider, useMealPlanner } from "./hooks/MealPlannerContext";
import { useAuth } from "./hooks/useAuth";
import { useAccentSync } from "./hooks/useAccentSync";
import { useIsMobile } from "./hooks/useIsMobile";
import { LoginScreen } from "./components/LoginScreen";
import { NoHouseholdScreen } from "./components/NoHouseholdScreen";
import { HomePage } from "./pages/HomePage";
import { RecipesPage } from "./pages/RecipesPage";
import { RecipePage } from "./pages/RecipePage";
import { CookPage } from "./pages/CookPage";
import { RecipeWriterPage, RecipeEditPage } from "./pages/RecipeWriterPage";
import { PlanPage } from "./pages/PlanPage";
import { SharedWithUsPage } from "./pages/SharedWithUsPage";
import { PublicPlansPage } from "./pages/PublicPlansPage";
import { GroceryPage } from "./pages/GroceryPage";
import { InventoryPage } from "./pages/InventoryPage";
import { PurveyorsPage } from "./pages/PurveyorsPage";
import { PurveyorPortalPage, PortalBoxesTab, PortalCatalogTab, PortalProfileTab } from "./pages/PurveyorPortalPage";
import { PlanDefaultsPage } from "./pages/PlanDefaultsPage";
import { AdminPage } from "./pages/AdminPage";
import { SharedRecipePage } from "./pages/SharedRecipePage";
import { SharedWeekPlanPage } from "./pages/SharedWeekPlanPage";
import { SharedWeekPlanDayPage } from "./pages/SharedWeekPlanDayPage";
import { FeedbackButton } from "./components/FeedbackButton";
import { SubtabLayout } from "./components/SubtabLayout";
import { AccountMenu, DesktopTabs, TabBar } from "./components/AppNav";
import { HOME_PATH, PLAN_SUBTABS, LEGACY_REDIRECTS } from "./lib/navigation";
import { isAppAdmin } from "./lib/db";
import { COLORS, FONT_SIZE, LAYOUT, SPACE } from "./lib/constants";

function CenteredMessage({ children }) {
  const { paper, ink, soft } = COLORS;
  return (
    <div style={{ background: paper, color: ink, fontFamily: "var(--font-serif)", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: SPACE.x4, boxSizing: "border-box" }}>
      <span style={{ color: soft, fontFamily: "var(--font-serif)" }}>{children}</span>
    </div>
  );
}

function AppShell({ userEmail, onSignOut, householdId }) {
  const { loaded, saveError, colors } = useMealPlanner();
  const { paper, ink, soft, danger, line } = colors;
  const isMobile = useIsMobile();
  // null until known. Only decides whether the admin pages show in the
  // menu; a failed check (or a database without 0051) just means none.
  const [isAdmin, setIsAdmin] = useState(null);
  // The route for an adminOnly page: nothing until the check returns, then
  // the page for an admin and a redirect for anyone else.
  const adminOnly = (element) => (isAdmin === null ? null : isAdmin ? element : <Navigate to={HOME_PATH} replace />);

  useEffect(() => {
    let cancelled = false;
    isAppAdmin()
      .then((v) => !cancelled && setIsAdmin(v))
      .catch(() => !cancelled && setIsAdmin(false));
    return () => {
      cancelled = true;
    };
  }, []);

  // On desktop the header (title, tabs, menu) stays pinned while a page
  // scrolls (feedback #54). Its height is published as --app-header-h so a
  // page's own pinned block (Recipes' search/filters, a subtab strip) can
  // sit directly beneath it. On a phone nothing is pinned at the top -- the
  // tabs are at the bottom -- so the variable is left unset (0).
  const stickyHeaderRef = useRef(null);
  useLayoutEffect(() => {
    const el = stickyHeaderRef.current;
    if (!el) return undefined;
    const root = document.documentElement;
    const publish = () => root.style.setProperty("--app-header-h", `${el.offsetHeight}px`);
    publish();
    const observer = new ResizeObserver(publish);
    observer.observe(el);
    return () => {
      observer.disconnect();
      root.style.removeProperty("--app-header-h");
    };
  }, [loaded, isMobile]);

  if (!loaded) {
    return <CenteredMessage>Loading your kitchen…</CenteredMessage>;
  }

  const today = new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });
  const menu = <AccountMenu variant={isMobile ? "phone" : "desktop"} userEmail={userEmail} isAdmin={isAdmin === true} onSignOut={onSignOut} />;
  const saveErrorLine = saveError && (
    <p className="mp-sans" role="alert" style={{ fontSize: FONT_SIZE.sm, color: danger, margin: "var(--space-md) 0 0" }}>
      Changes aren't saving right now — check your connection.
    </p>
  );

  const header = isMobile ? (
    <header style={{ marginBottom: SPACE.x3 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: SPACE.xl }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: FONT_SIZE.h1, fontWeight: 600, lineHeight: 1.1 }}>The Weekly Kitchen</div>
          <div className="mp-sans" style={{ fontSize: FONT_SIZE.sm, color: soft, marginTop: SPACE.hair }}>{today}</div>
        </div>
        {menu}
      </div>
      {saveErrorLine}
    </header>
  ) : (
    <header ref={stickyHeaderRef} style={{ position: "sticky", top: 0, zIndex: 20, background: paper, marginBottom: SPACE.x4, borderBottom: `1px solid ${line}` }}>
      <div className="app-header-row">
        <div style={{ fontSize: FONT_SIZE.h1, fontWeight: 600, lineHeight: 1.1, flexShrink: 0 }}>The Weekly Kitchen</div>
        <DesktopTabs />
        <div className="app-header-menu">{menu}</div>
      </div>
      {saveErrorLine && <div style={{ paddingBottom: SPACE.md }}>{saveErrorLine}</div>}
    </header>
  );

  return (
    <div
      style={{
        background: paper,
        color: ink,
        fontFamily: "var(--font-serif)",
        minHeight: "100vh",
        // Phone: the design's 20px side gutter, and room at the bottom for
        // the fixed tab bar (plus the home-indicator inset).
        padding: isMobile ? "var(--space-x3) var(--space-x3) 0" : "0 var(--space-x4) var(--space-x4)",
        paddingBottom: isMobile ? `calc(${LAYOUT.tabBarHeight}px + env(safe-area-inset-bottom) + var(--space-x4))` : undefined,
        boxSizing: "border-box",
      }}
    >
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        {header}

        <Routes>
          <Route path="/" element={<Navigate to={HOME_PATH} replace />} />
          <Route path={HOME_PATH} element={<HomePage isAdmin={isAdmin === true} />} />
          <Route path="/recipes" element={<RecipesPage />} />
          <Route path="/recipes/new" element={<RecipeWriterPage key="new" mode="new" />} />
          <Route path="/recipes/import" element={<RecipeWriterPage key="import" mode="import" />} />
          <Route path="/recipes/:id" element={<RecipePage />} />
          <Route path="/recipes/:id/edit" element={<RecipeEditPage />} />
          <Route path="/recipes/:id/cook" element={<CookPage />} />
          <Route path="/plan" element={<SubtabLayout tabs={PLAN_SUBTABS} label="Plan sections" />}>
            <Route index element={<PlanPage />} />
            <Route path="shared" element={<SharedWithUsPage />} />
            <Route path="public" element={<PublicPlansPage />} />
          </Route>
          {LEGACY_REDIRECTS.map(({ from, to }) => (
            <Route key={from} path={from} element={<Navigate to={to} replace />} />
          ))}
          <Route path="/grocery" element={<GroceryPage />} />
          <Route path="/inventory" element={adminOnly(<InventoryPage />)} />
          <Route path="/purveyors" element={adminOnly(<PurveyorsPage />)} />
          <Route path="/purveyor-portal" element={adminOnly(<PurveyorPortalPage />)}>
            <Route index element={<PortalBoxesTab />} />
            <Route path="catalog" element={<PortalCatalogTab />} />
            <Route path="profile" element={<PortalProfileTab />} />
          </Route>
          <Route path="/defaults" element={<PlanDefaultsPage />} />
          <Route path="/admin" element={adminOnly(<AdminPage />)} />
          <Route path="*" element={<Navigate to={HOME_PATH} replace />} />
        </Routes>
      </div>
      {isMobile && <TabBar />}
      <FeedbackButton householdId={householdId} email={userEmail} />
    </div>
  );
}

// A share link (see RecipeSharing.jsx's "+ Create share link" and
// supabase/migrations/0013_recipe_share_links_and_shared_by.sql) has to
// render for a signed-out visitor, so it's routed here -- above the
// sign-in gate below -- rather than as one of AppShell's <Routes>, which
// only ever render once a session exists.
export default function App() {
  return (
    <Routes>
      <Route path="/shared/:token" element={<SharedRecipePage />} />
      <Route path="/shared-plan/:token" element={<SharedWeekPlanPage />} />
      <Route path="/shared-day/:token" element={<SharedWeekPlanDayPage />} />
      <Route path="*" element={<AuthenticatedApp />} />
    </Routes>
  );
}

function AuthenticatedApp() {
  const { authLoading, session, userId, householdId, householdLoading, householdResolved, householdError, reloadHousehold, signInWithOtp, signOut } = useAuth();
  useAccentSync(session);

  if (authLoading) return <CenteredMessage>Loading…</CenteredMessage>;
  if (!session) return <LoginScreen onSignIn={signInWithOtp} />;
  if (householdLoading || !householdResolved) return <CenteredMessage>Setting up your household…</CenteredMessage>;
  // A failed lookup must not fall through to the "no household" screen below,
  // which offers to create one -- that would be wrong for someone who has a
  // household and merely lost their connection.
  if (householdError) {
    return (
      <CenteredMessage>
        Couldn't load your household.{" "}
        <button type="button" onClick={reloadHousehold} style={{ background: "none", border: "none", color: COLORS.accent, textDecoration: "underline", cursor: "pointer", padding: 0, font: "inherit" }}>
          Try again
        </button>
      </CenteredMessage>
    );
  }
  // Signed in but in no household: an invited person who hasn't accepted yet.
  if (!householdId) return <NoHouseholdScreen userEmail={session.user.email} onSignOut={signOut} onReload={reloadHousehold} />;

  return (
    <MealPlannerProvider householdId={householdId} userId={userId}>
      <AppShell userEmail={session.user.email} onSignOut={signOut} householdId={householdId} />
    </MealPlannerProvider>
  );
}
